import { useAddInfluencerCost } from "@entities/brief/lib/hooks/useAddInfluencerCost";
import { useAddInfluencerInitialCost } from "@entities/brief/lib/hooks/useAddInfluencerInitialCost";
import { PRICING_POC_FACETS_QUERY_KEY } from "@entities/pricingWorkload/lib/constants";
import BrandPaymentTerms from "@features/BrandPaymentTerms";
import { ToastService } from "@shared/lib/services";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { inrAmountWordsFromText } from "@shared/lib/utils/inrAmountInWords";
import { InrAmountWordsLine, Tooltip } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Form, InputNumber, Space } from "antd";
import { useEffect } from "react";
import { CommercialsActionsMenu } from "./CommercialsActionsMenu";
import styles from "./styles.module.scss";

export default function AddCommercials({
  briefId,
  briefInfluencer,
  type,
  pocId,
}: {
  briefId: string;
  briefInfluencer: IBriefInfluencerV2;
  type: "create-new" | "create";
  pocId: string;
}) {
  const queryClient = useQueryClient();
  const briefInfluencerId = briefInfluencer._id;

  const { addInitialCost, isAdded, isAdding } = useAddInfluencerInitialCost(
    briefId,
    () => {
      queryClient.invalidateQueries({
        queryKey: PRICING_POC_FACETS_QUERY_KEY(briefId, pocId),
      });
    }
  );
  const {
    addCost,
    isAdded: isAddedCost,
    isAdding: isAddingCost,
  } = useAddInfluencerCost(briefId, () => {
    queryClient.invalidateQueries({
      queryKey: PRICING_POC_FACETS_QUERY_KEY(briefId, pocId),
    });
  });
  const [form] = Form.useForm();
  const cost = Form.useWatch("cost", form);
  const hasCostValue =
    cost !== null && cost !== undefined && !Number.isNaN(Number(cost));
  const amountWords = inrAmountWordsFromText(cost);

  useEffect(() => {
    if (isAddedCost || isAdded) {
      ToastService.success("Cost added successfully.");
    }
  }, [isAddedCost, isAdded]);

  return (
    <Form
      form={form}
      onFinish={(values) => {
        if (type === "create-new") {
          addCost({
            cost: parseInt(values.cost) || 0,
            briefInfluencerId: briefInfluencerId,
          });
        } else {
          addInitialCost({
            cost: parseInt(values.cost) || 0,
            briefInfluencerId: briefInfluencerId,
          });
        }
      }}
    >
      <Space.Compact>
        <Form.Item
          name="cost"
          required={false}
          style={{ marginBottom: 0 }}
          rules={[
            {
              required: true,
              message: "Please enter commercials",
            },
          ]}
          extra={
            <Flex vertical gap={2} className={styles.commercialsExtra}>
              <div className={styles.amountWordsSlot}>
                <Tooltip title={amountWords || undefined}>
                  <div className={styles.amountWordsContent}>
                    <InrAmountWordsLine
                      value={cost}
                      className={styles.amountInWords}
                    />
                  </div>
                </Tooltip>
              </div>
              <span className={styles.paymentTermsText}>
                Payment terms:{" "}
                <BrandPaymentTerms
                  briefId={briefId}
                  briefInfluencer={briefInfluencer}
                  updateBriefInfluencer={() => {
                    queryClient.invalidateQueries({
                      queryKey: PRICING_POC_FACETS_QUERY_KEY(briefId, pocId),
                    });
                  }}
                />
              </span>
            </Flex>
          }
        >
          <InputNumber
            placeholder="Enter commercials"
            prefix={"₹"}
            type="number"
            min={0}
            style={{ width: "100%" }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          />
        </Form.Item>
        <CommercialsActionsMenu
          briefId={briefId}
          briefInfluencer={briefInfluencer}
          pocId={pocId}
          compact={false}
        />
      </Space.Compact>
      <div className={styles.saveButtonSlot}>
        <Button
          size="small"
          type="primary"
          htmlType="submit"
          loading={isAddingCost || isAdding}
          disabled={!hasCostValue}
          className={styles.saveButton}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => event.stopPropagation()}
        >
          Save
        </Button>
      </div>
    </Form>
  );
}
